<template>
  <div class="teacher-atten-root">
    <div class="teacher-atten-screen" :style="screenStyle">
      <div class="screen-bg">
        <div class="bg-grid"></div>
        <div class="bg-glow bg-glow--top"></div>
        <div class="bg-glow bg-glow--bottom"></div>
      </div>

      <header class="screen-header">
        <div class="header-inner">
          <div class="header-side header-side--left">
            <div class="school-block">
              <span class="school-mark"></span>
              <div class="school-body">
                <span class="school-name">{{ screenData.schoolName || "—" }}</span>
              </div>
              <span class="school-tail">
                <span class="deco-dot"></span>
                <span class="school-tail-line"></span>
              </span>
            </div>
          </div>

          <div class="header-center">
            <div class="center-deco center-deco--left">
              <span class="deco-bar"></span>
              <span class="deco-dot"></span>
            </div>
            <div class="center-body">
              <h1 class="title">教师考勤大屏</h1>
              <div class="date-row">
                <span class="date-trigger-wrap">
                  <span class="date-trigger-text">{{ displayDate }}</span>
                  <el-date-picker
                    v-model="selectedDate"
                    type="date"
                    value-format="yyyy-MM-dd"
                    :clearable="false"
                    :editable="false"
                    prefix-icon=""
                    class="date-picker-overlay"
                    popper-class="teacher-atten-date-popper"
                    @change="handleDateChange"
                  />
                </span>
              </div>
            </div>
            <div class="center-deco center-deco--right">
              <span class="deco-dot"></span>
              <span class="deco-bar"></span>
            </div>
          </div>

          <div class="header-side header-side--right">
            <div class="clock-block">
              <span class="clock-time">{{ currentTime }}</span>
            </div>
            <span v-if="loading" class="header-loading-inline">刷新中</span>
          </div>
        </div>
        <div class="header-line">
          <span class="header-line-glow"></span>
        </div>
      </header>

      <section class="kpi-row">
        <div
          v-for="item in kpiList"
          :key="item.label"
          class="kpi-card"
          :class="'kpi-card--' + item.tone"
        >
          <div class="kpi-accent"></div>
          <div class="kpi-body">
            <div class="kpi-value" :style="{ color: item.color }">{{ item.value }}</div>
            <div class="kpi-label">{{ item.label }}</div>
          </div>
        </div>
      </section>

      <section class="chart-row">
        <div class="panel">
          <div class="panel-corner panel-corner--tl"></div>
          <div class="panel-corner panel-corner--tr"></div>
          <div class="panel-corner panel-corner--bl"></div>
          <div class="panel-corner panel-corner--br"></div>
          <div class="panel-title">
            <span class="panel-title-bar"></span>
            {{ dayLabel }}考勤分布
          </div>
          <div ref="statusChart" class="chart-box"></div>
        </div>
        <div class="panel panel-wide">
          <div class="panel-corner panel-corner--tl"></div>
          <div class="panel-corner panel-corner--tr"></div>
          <div class="panel-corner panel-corner--bl"></div>
          <div class="panel-corner panel-corner--br"></div>
          <div class="panel-title">
            <span class="panel-title-bar"></span>
            本周到勤 / 迟到趋势
          </div>
          <div ref="weekChart" class="chart-box"></div>
        </div>
        <div class="panel">
          <div class="panel-corner panel-corner--tl"></div>
          <div class="panel-corner panel-corner--tr"></div>
          <div class="panel-corner panel-corner--bl"></div>
          <div class="panel-corner panel-corner--br"></div>
          <div class="panel-title">
            <span class="panel-title-bar"></span>
            {{ dayLabel }}打卡时段分布
          </div>
          <div ref="hourChart" class="chart-box"></div>
        </div>
      </section>

      <section class="table-row">
        <div class="panel panel-table">
          <div class="panel-corner panel-corner--tl"></div>
          <div class="panel-corner panel-corner--tr"></div>
          <div class="panel-corner panel-corner--bl"></div>
          <div class="panel-corner panel-corner--br"></div>
          <div class="panel-title">
            <span class="panel-title-bar"></span>
            迟到教师
            <span class="panel-count">{{ lateList.length }}</span>
          </div>
          <screen-auto-scroll-table variant="screen" :columns="nameDeptColumns" :rows="lateList" :body-height="tableBodyHeight" />
        </div>
        <div class="panel panel-table">
          <div class="panel-corner panel-corner--tl"></div>
          <div class="panel-corner panel-corner--tr"></div>
          <div class="panel-corner panel-corner--bl"></div>
          <div class="panel-corner panel-corner--br"></div>
          <div class="panel-title">
            <span class="panel-title-bar"></span>
            缺勤教师
            <span class="panel-count panel-count--warn">{{ missList.length }}</span>
          </div>
          <screen-auto-scroll-table variant="screen" :columns="nameDeptColumns" :rows="missList" :body-height="tableBodyHeight" />
        </div>
        <div class="panel panel-table">
          <div class="panel-corner panel-corner--tl"></div>
          <div class="panel-corner panel-corner--tr"></div>
          <div class="panel-corner panel-corner--bl"></div>
          <div class="panel-corner panel-corner--br"></div>
          <div class="panel-title">
            <span class="panel-title-bar"></span>
            {{ dayLabel }}到勤记录
          </div>
          <screen-auto-scroll-table variant="screen" :columns="arrivedColumns" :rows="arrivedList" :body-height="tableBodyHeight" />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import echarts from "echarts";
import { getTeacherAttenBigScreenData } from "../../api/teacherAttendance";
import ScreenAutoScrollTable from "../../components/ScreenAutoScrollTable.vue";

const WEEK_LABELS = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];
const DESIGN_WIDTH = 1920;
const DESIGN_HEIGHT = 1080;
const TABLE_BODY_HEIGHT = 460;

/** 大屏设计令牌（Dark OLED + 数据蓝） */
const THEME = {
  primary: "#3B82F6",
  accent: "#22D3EE",
  amber: "#F59E0B",
  success: "#34D399",
  warning: "#FBBF24",
  danger: "#F87171",
  muted: "#64748B",
  info: "#60A5FA",
  text: "#E2E8F0",
  textMuted: "#94A3B8",
  panelBg: "rgba(15, 23, 42, 0.72)",
  axis: "#334155",
  split: "rgba(51, 65, 85, 0.45)",
};

const CHART_COLORS = [THEME.success, THEME.warning, THEME.danger, THEME.muted, THEME.info];

export default {
  name: "TeacherAttenBigScreenView",
  components: { ScreenAutoScrollTable },
  data() {
    return {
      departmentId: "0",
      selectedDate: "",
      loading: false,
      currentTime: "",
      screenData: {},
      timer: null,
      clockTimer: null,
      statusChartInst: null,
      weekChartInst: null,
      hourChartInst: null,
      nameDeptColumns: [
        { label: "姓名", key: "stuName" },
        { label: "部门", key: "deptName" },
      ],
      tableBodyHeight: TABLE_BODY_HEIGHT,
      screenScaleX: 1,
      screenScaleY: 1,
      screenOffsetX: 0,
      screenOffsetY: 0,
    };
  },
  computed: {
    screenStyle() {
      return {
        transform: `translate(${this.screenOffsetX}px, ${this.screenOffsetY}px) scale(${this.screenScaleX}, ${this.screenScaleY})`,
      };
    },
    c1() {
      return this.screenData.category1 || {};
    },
    kpiList() {
      const total = this.screenData.stuCount || 0;
      const arrived = (this.c1.normal || 0) + (this.c1.late || 0) + (this.c1.equit || 0);
      const arriveRate = total > 0 ? Math.round((arrived / total) * 100) : 0;
      return [
        { label: "教师总数", value: total, color: THEME.accent, tone: "cyan" },
        { label: "到勤率", value: arriveRate + "%", color: THEME.success, tone: "green" },
        { label: "正常", value: this.c1.normal || 0, color: THEME.success, tone: "green" },
        { label: "迟到", value: this.c1.late || 0, color: THEME.warning, tone: "amber" },
        { label: "早退", value: this.c1.equit || 0, color: THEME.danger, tone: "red" },
        { label: "缺勤", value: this.c1.miss || 0, color: THEME.muted, tone: "gray" },
        { label: "请假", value: this.c1.leave || 0, color: THEME.info, tone: "blue" },
      ];
    },
    lateList() {
      return this.screenData.table1 || [];
    },
    missList() {
      return this.screenData.table2 || [];
    },
    arrivedList() {
      return this.screenData.table4 || [];
    },
    displayDate() {
      return this.screenData.date || this.selectedDate || this.formatDate(new Date());
    },
    dayLabel() {
      const today = this.formatDate(new Date());
      return this.selectedDate === today ? "今日" : this.selectedDate;
    },
    arrivedColumns() {
      const formatTime = this.formatTime;
      return [
        { label: "姓名", key: "stuName" },
        { label: "部门", key: "deptName" },
        { label: "时间", key: "time", formatter: (row) => formatTime(row.time) },
      ];
    },
  },
  mounted() {
    const fromRoute = this.$route.query.departmentId;
    const fromUrl = new URLSearchParams(window.location.search).get("departmentId");
    this.departmentId = fromRoute || fromUrl || "99";
    if (this.departmentId === "0") this.departmentId = "99";
    const fromRouteDate = this.$route.query.date;
    const fromUrlDate = new URLSearchParams(window.location.search).get("date");
    this.selectedDate = fromRouteDate || fromUrlDate || this.formatDate(new Date());
    this.tickClock();
    this.clockTimer = setInterval(this.tickClock, 1000);
    this.$nextTick(() => {
      this.updateScale();
      this.initCharts();
      this.loadData();
      this.timer = setInterval(this.loadData, 60 * 1000);
      window.addEventListener("resize", this.handleResize);
    });
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
    if (this.clockTimer) clearInterval(this.clockTimer);
    window.removeEventListener("resize", this.handleResize);
    this.disposeCharts();
  },
  methods: {
    updateScale() {
      // 等比缩放，避免宽高比不一致时坐标轴文字被压扁。最后更新：2026-08-30
      const sx = window.innerWidth / DESIGN_WIDTH;
      const sy = window.innerHeight / DESIGN_HEIGHT;
      const scale = Math.min(sx, sy);
      this.screenScaleX = scale;
      this.screenScaleY = scale;
      this.screenOffsetX = (window.innerWidth - DESIGN_WIDTH * scale) / 2;
      this.screenOffsetY = (window.innerHeight - DESIGN_HEIGHT * scale) / 2;
    },
    tickClock() {
      const d = new Date();
      this.currentTime = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
    },
    formatDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    formatDateTime(d) {
      return `${this.formatDate(d)} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
    },
    formatTime(val) {
      if (!val) return "-";
      const s = String(val);
      return s.length >= 16 ? s.substring(11, 19) : s;
    },
    chartTooltip() {
      return {
        trigger: "item",
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        borderColor: "rgba(59, 130, 246, 0.35)",
        textStyle: { color: THEME.text, fontSize: 13 },
      };
    },
    axisTooltip() {
      return {
        trigger: "axis",
        backgroundColor: "rgba(15, 23, 42, 0.95)",
        borderColor: "rgba(59, 130, 246, 0.35)",
        textStyle: { color: THEME.text, fontSize: 13 },
      };
    },
    initCharts() {
      if (this.$refs.statusChart) this.statusChartInst = echarts.init(this.$refs.statusChart);
      if (this.$refs.weekChart) this.weekChartInst = echarts.init(this.$refs.weekChart);
      if (this.$refs.hourChart) this.hourChartInst = echarts.init(this.$refs.hourChart);
    },
    disposeCharts() {
      [this.statusChartInst, this.weekChartInst, this.hourChartInst].forEach((c) => {
        if (c) c.dispose();
      });
    },
    handleResize() {
      this.updateScale();
      this.$nextTick(() => {
        [this.statusChartInst, this.weekChartInst, this.hourChartInst].forEach((c) => {
          if (c) c.resize();
        });
      });
    },
    handleDateChange(val) {
      if (!val) return;
      this.selectedDate = val;
      const query = { ...this.$route.query, date: val };
      this.$router.replace({ path: this.$route.path, query }).catch(() => {});
      this.loadData();
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await getTeacherAttenBigScreenData({
          departmentId: this.departmentId,
          isAdmin: 0,
          date: this.selectedDate,
        });
        if (res && res.success) {
          this.screenData = res.response || {};
          this.renderCharts();
        }
      } catch (e) {
        console.error("[TeacherAttenBigScreen] loadData", e);
      } finally {
        this.loading = false;
      }
    },
    renderCharts() {
      this.renderStatusChart();
      this.renderWeekChart();
      this.renderHourChart();
    },
    renderStatusChart() {
      if (!this.statusChartInst) return;
      const data = [
        { name: "正常", value: this.c1.normal || 0 },
        { name: "迟到", value: this.c1.late || 0 },
        { name: "早退", value: this.c1.equit || 0 },
        { name: "缺勤", value: this.c1.miss || 0 },
        { name: "请假", value: this.c1.leave || 0 },
      ].filter((d) => d.value > 0);
      this.statusChartInst.setOption({
        color: CHART_COLORS,
        tooltip: this.chartTooltip(),
        series: [{
          type: "pie",
          radius: ["42%", "68%"],
          center: ["50%", "54%"],
          label: { color: THEME.textMuted, fontSize: 13 },
          itemStyle: { borderColor: "rgba(11, 15, 25, 0.9)", borderWidth: 3 },
          data: data.length ? data : [{ name: "暂无数据", value: 1, itemStyle: { color: "rgba(51, 65, 85, 0.6)" } }],
        }],
      });
    },
    renderWeekChart() {
      if (!this.weekChartInst) return;
      const c3 = this.screenData.category3 || { atten: [], late: [] };
      const len = Math.max((c3.atten || []).length, (c3.late || []).length, 7);
      const labels = [];
      for (let i = 0; i < len; i++) labels.push(WEEK_LABELS[i] || `D${i + 1}`);
      this.weekChartInst.setOption({
        color: [THEME.success, THEME.amber],
        tooltip: this.axisTooltip(),
        legend: {
          data: ["到勤率%", "迟到率%"],
          textStyle: { color: THEME.textMuted, fontSize: 16, fontWeight: 500 },
          top: 0,
          itemWidth: 18,
          itemHeight: 10,
          itemGap: 20,
        },
        grid: { left: 44, right: 16, top: 40, bottom: 36 },
        xAxis: {
          type: "category",
          data: labels,
          axisLine: { lineStyle: { color: THEME.axis } },
          axisLabel: {
            color: THEME.textMuted,
            fontSize: 14,
            fontFamily: "Microsoft YaHei, Fira Sans, sans-serif",
            margin: 12,
            lineHeight: 18,
          },
        },
        yAxis: {
          type: "value",
          max: 100,
          axisLine: { show: false },
          splitLine: { lineStyle: { color: THEME.split, type: "dashed" } },
          axisLabel: {
            color: THEME.textMuted,
            fontSize: 13,
            fontFamily: "Microsoft YaHei, Fira Sans, sans-serif",
          },
        },
        series: [
          {
            name: "到勤率%",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            lineStyle: { width: 2.5 },
            areaStyle: { color: "rgba(52, 211, 153, 0.12)" },
            data: c3.atten || [],
          },
          {
            name: "迟到率%",
            type: "line",
            smooth: true,
            symbol: "circle",
            symbolSize: 6,
            lineStyle: { width: 2.5 },
            areaStyle: { color: "rgba(245, 158, 11, 0.1)" },
            data: c3.late || [],
          },
        ],
      });
    },
    renderHourChart() {
      if (!this.hourChartInst) return;
      const list = this.screenData.category2 || [];
      this.hourChartInst.setOption({
        tooltip: this.axisTooltip(),
        grid: { left: 40, right: 12, top: 12, bottom: 36 },
        xAxis: {
          type: "category",
          data: list.map((x) => x.name),
          axisLabel: {
            color: THEME.textMuted,
            interval: 2,
            fontSize: 13,
            fontFamily: "Microsoft YaHei, Fira Sans, sans-serif",
            margin: 12,
            lineHeight: 18,
          },
          axisLine: { lineStyle: { color: THEME.axis } },
        },
        yAxis: {
          type: "value",
          axisLine: { show: false },
          splitLine: { lineStyle: { color: THEME.split, type: "dashed" } },
          axisLabel: {
            color: THEME.textMuted,
            fontSize: 13,
            fontFamily: "Microsoft YaHei, Fira Sans, sans-serif",
          },
        },
        series: [{
          type: "bar",
          data: list.map((x) => x.value),
          barMaxWidth: 16,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: "#38BDF8" },
              { offset: 1, color: "#1E40AF" },
            ]),
            borderRadius: [3, 3, 0, 0],
          },
        }],
      });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Fira+Code:wght@500;600;700&family=Fira+Sans:wght@400;500;600&display=swap");

.teacher-atten-root {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  position: relative;
  background: #0b0f19;
}

.teacher-atten-screen {
  width: 1920px;
  height: 1080px;
  position: absolute;
  left: 0;
  top: 0;
  transform-origin: left top;
  box-sizing: border-box;
  padding: 16px 28px 20px;
  color: #e2e8f0;
  font-family: "Fira Sans", "Microsoft YaHei", sans-serif;
  overflow: hidden;
  display: grid;
  grid-template-rows: 88px 94px 340px 1fr;
  gap: 14px;
}

/* ---- 背景层 ---- */
.screen-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(59, 130, 246, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse 90% 80% at 50% 40%, black 20%, transparent 75%);
}

.bg-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
}

.bg-glow--top {
  width: 700px;
  height: 400px;
  top: -120px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(30, 64, 175, 0.18);
}

.bg-glow--bottom {
  width: 500px;
  height: 300px;
  bottom: -80px;
  right: 10%;
  background: rgba(34, 211, 238, 0.06);
}

.teacher-atten-screen > *:not(.screen-bg) {
  position: relative;
  z-index: 1;
}

/* ---- 顶栏 ---- */
.screen-header {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 0;
}

.header-inner {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.header-side--left {
  display: flex;
  align-items: center;
  padding-left: 4px;
  min-width: 0;
}

.school-block {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

.school-mark {
  width: 4px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 2px;
  background: linear-gradient(180deg, #38bdf8, #3b82f6);
  box-shadow: 0 0 12px rgba(56, 189, 248, 0.45);
}

.school-body {
  min-width: 0;
  flex-shrink: 1;
}

.school-name {
  display: block;
  font-size: 24px;
  font-weight: 600;
  color: #f1f5f9;
  letter-spacing: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 0 18px rgba(59, 130, 246, 0.35);
}

.school-tail {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.school-tail-line {
  width: 48px;
  height: 2px;
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.7), transparent);
}

.header-side--right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  padding-right: 4px;
}

.clock-block {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.clock-time {
  font-family: "Fira Code", monospace;
  font-variant-numeric: tabular-nums;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: 3px;
  color: #38bdf8;
  text-shadow: 0 0 20px rgba(56, 189, 248, 0.5);
  white-space: nowrap;
  line-height: 1;
}

.header-loading-inline {
  font-size: 12px;
  color: #38bdf8;
  animation: pulse 1.6s ease-in-out infinite;
}

.header-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-width: 0;
}

.center-deco {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 48px;
}

.center-deco--right {
  flex-direction: row-reverse;
}

.deco-bar {
  flex: 1;
  height: 2px;
  max-width: 140px;
  background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.75));
}

.center-deco--right .deco-bar {
  background: linear-gradient(90deg, rgba(59, 130, 246, 0.75), transparent);
}

.deco-dot {
  width: 8px;
  height: 8px;
  border: 2px solid #38bdf8;
  transform: rotate(45deg);
  box-shadow: 0 0 8px rgba(56, 189, 248, 0.6);
  flex-shrink: 0;
}

.center-body {
  text-align: center;
  flex-shrink: 0;
}

.title {
  margin: 0;
  font-family: "Fira Sans", "Microsoft YaHei", sans-serif;
  font-size: 34px;
  font-weight: 600;
  letter-spacing: 10px;
  color: #f8fafc;
  text-shadow: 0 0 28px rgba(59, 130, 246, 0.5);
  line-height: 1.1;
}

.date-row {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
}

.header-line {
  position: relative;
  height: 1px;
  margin-top: 10px;
  background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.55) 20%, rgba(56, 189, 248, 0.8) 50%, rgba(59, 130, 246, 0.55) 80%, transparent);
}

.header-line-glow {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 320px;
  height: 6px;
  background: radial-gradient(ellipse, rgba(56, 189, 248, 0.45), transparent 70%);
  pointer-events: none;
}

@keyframes pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

.date-trigger-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 4px 16px;
  border: 1px solid rgba(56, 189, 248, 0.35);
  border-radius: 4px;
  background: rgba(56, 189, 248, 0.06);
  transition: border-color 0.2s, background 0.2s;
}

.date-trigger-wrap:hover {
  border-color: rgba(56, 189, 248, 0.6);
  background: rgba(56, 189, 248, 0.12);
}

.date-trigger-text {
  cursor: pointer;
  font-family: "Fira Code", monospace;
  font-variant-numeric: tabular-nums;
  font-size: 16px;
  letter-spacing: 1px;
  color: #7dd3fc;
  transition: color 0.2s;
}

.date-trigger-wrap:hover .date-trigger-text {
  color: #bae6fd;
}

.date-picker-overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.date-picker-overlay >>> .el-input__inner {
  height: 100%;
  cursor: pointer;
  padding: 0;
  border: none;
  background: transparent;
}

/* ---- KPI ---- */
.kpi-row {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 14px;
}

.kpi-card {
  position: relative;
  display: flex;
  background: rgba(15, 23, 42, 0.65);
  border: 1px solid rgba(51, 65, 85, 0.6);
  border-radius: 8px;
  overflow: hidden;
  backdrop-filter: blur(8px);
}

.kpi-accent {
  width: 4px;
  flex-shrink: 0;
}

.kpi-card--cyan .kpi-accent { background: linear-gradient(180deg, #22d3ee, #0891b2); }
.kpi-card--green .kpi-accent { background: linear-gradient(180deg, #34d399, #059669); }
.kpi-card--amber .kpi-accent { background: linear-gradient(180deg, #fbbf24, #d97706); }
.kpi-card--red .kpi-accent { background: linear-gradient(180deg, #f87171, #dc2626); }
.kpi-card--gray .kpi-accent { background: linear-gradient(180deg, #94a3b8, #64748b); }
.kpi-card--blue .kpi-accent { background: linear-gradient(180deg, #60a5fa, #2563eb); }

.kpi-body {
  flex: 1;
  padding: 14px 12px;
  text-align: center;
}

.kpi-value {
  font-family: "Fira Code", monospace;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.kpi-label {
  margin-top: 8px;
  font-size: 14px;
  color: #94a3b8;
  letter-spacing: 0.5px;
}

/* ---- 面板 ---- */
.chart-row {
  display: grid;
  grid-template-columns: 1fr 1.55fr 1fr;
  gap: 14px;
  min-height: 0;
}

.table-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  min-height: 0;
}

.panel {
  position: relative;
  background: rgba(15, 23, 42, 0.72);
  border: 1px solid rgba(51, 65, 85, 0.55);
  border-radius: 8px;
  padding: 14px 16px;
  min-height: 0;
  backdrop-filter: blur(6px);
}

.chart-row .panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.panel-table {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.panel-corner {
  position: absolute;
  width: 14px;
  height: 14px;
  pointer-events: none;
}

.panel-corner--tl { top: -1px; left: -1px; border-top: 2px solid #3b82f6; border-left: 2px solid #3b82f6; }
.panel-corner--tr { top: -1px; right: -1px; border-top: 2px solid #3b82f6; border-right: 2px solid #3b82f6; }
.panel-corner--bl { bottom: -1px; left: -1px; border-bottom: 2px solid #3b82f6; border-left: 2px solid #3b82f6; }
.panel-corner--br { bottom: -1px; right: -1px; border-bottom: 2px solid #3b82f6; border-right: 2px solid #3b82f6; }

.panel-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 19px;
  font-weight: 600;
  color: #f1f5f9;
  margin-bottom: 12px;
  flex-shrink: 0;
  letter-spacing: 0.5px;
}

.panel-title-bar {
  width: 4px;
  height: 20px;
  background: linear-gradient(180deg, #38bdf8, #1e40af);
  border-radius: 2px;
  flex-shrink: 0;
}

.panel-count {
  margin-left: auto;
  font-family: "Fira Code", monospace;
  font-size: 16px;
  color: #38bdf8;
  background: rgba(59, 130, 246, 0.12);
  border: 1px solid rgba(59, 130, 246, 0.25);
  border-radius: 4px;
  padding: 2px 12px;
}

.panel-count--warn {
  color: #fbbf24;
  background: rgba(245, 158, 11, 0.12);
  border-color: rgba(245, 158, 11, 0.25);
}

.chart-box {
  flex: 1;
  min-height: 260px;
}

.panel-table >>> .auto-scroll-table {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.panel-table >>> .auto-scroll-viewport {
  flex: 1;
}
</style>

<style>
/* 日期选择器深色主题（挂载到 body，不可 scoped） */
.teacher-atten-date-popper.el-picker-panel {
  background: #0f172a;
  border: 1px solid rgba(59, 130, 246, 0.45);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 24px rgba(59, 130, 246, 0.15);
  color: #e2e8f0;
}

.teacher-atten-date-popper .el-picker-panel__body-wrapper,
.teacher-atten-date-popper .el-picker-panel__body {
  background: #0f172a;
}

.teacher-atten-date-popper .el-date-picker__header {
  border-bottom-color: rgba(59, 130, 246, 0.25);
}

.teacher-atten-date-popper .el-picker-panel__icon-btn {
  color: #94a3b8;
}

.teacher-atten-date-popper .el-picker-panel__icon-btn:hover {
  color: #38bdf8;
}

.teacher-atten-date-popper .el-date-picker__header-label {
  color: #e2e8f0;
  font-weight: 600;
}

.teacher-atten-date-popper .el-date-picker__header-label:hover {
  color: #38bdf8;
}

.teacher-atten-date-popper .el-date-table th {
  color: #64748b;
  border-bottom-color: rgba(59, 130, 246, 0.15);
}

.teacher-atten-date-popper .el-date-table td {
  color: #cbd5e1;
}

.teacher-atten-date-popper .el-date-table td.next-month,
.teacher-atten-date-popper .el-date-table td.prev-month {
  color: #475569;
}

.teacher-atten-date-popper .el-date-table td.available:hover span {
  background: rgba(59, 130, 246, 0.25);
  color: #f1f5f9;
}

.teacher-atten-date-popper .el-date-table td.today span {
  color: #38bdf8;
  font-weight: 600;
}

.teacher-atten-date-popper .el-date-table td.current:not(.disabled) span {
  background: #3b82f6;
  color: #fff;
  box-shadow: 0 0 12px rgba(59, 130, 246, 0.5);
}

.teacher-atten-date-popper .el-date-table td.disabled span {
  color: #334155;
  background: transparent;
}

.teacher-atten-date-popper .el-picker-panel__footer {
  background: #0f172a;
  border-top-color: rgba(59, 130, 246, 0.25);
}

.teacher-atten-date-popper .el-button--text {
  color: #94a3b8;
}

.teacher-atten-date-popper .el-button--text:hover {
  color: #38bdf8;
}

.teacher-atten-date-popper .popper__arrow::after {
  border-bottom-color: #0f172a !important;
}
</style>
